/**
 * Resolve which anatomical plane a contour annotation lives on.
 *
 * Resolution order:
 *   1. `_plane` stamp set by the ANNOTATION_COMPLETED listener in VolumeViewport
 *      for user-drawn strokes.
 *   2. `metadata.viewPlaneNormal` classification — region-grow companion
 *      contours skip the completion event, so they have no stamp; the
 *      camera's view-plane normal tells us which orientation.
 *
 * Returns null when neither source is usable so callers can decide whether
 * to skip the contour (preferred) or surface a warning, instead of silently
 * misclassifying it as axial.
 */
export type AnatomicalPlane = "axial" | "coronal" | "sagittal";

export function resolveAnnotationPlane(ann: {
  _plane?: unknown;
  metadata?: { viewPlaneNormal?: unknown };
}): AnatomicalPlane | null {
  const stamp = ann?._plane;
  if (typeof stamp === "string") {
    const lowered = stamp.toLowerCase();
    if (lowered === "axial" || lowered === "coronal" || lowered === "sagittal") {
      return lowered;
    }
  }

  const n = ann?.metadata?.viewPlaneNormal;
  if (!Array.isArray(n) || n.length !== 3) return null;
  const [x, y, z] = n;
  if (typeof x !== "number" || typeof y !== "number" || typeof z !== "number") {
    return null;
  }
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
    return null;
  }

  const ax = Math.abs(x);
  const ay = Math.abs(y);
  const az = Math.abs(z);
  if (ax + ay + az < 1e-6) return null; // zero vector — not a real normal

  if (az >= ax && az >= ay) return "axial";
  if (ay >= ax) return "coronal";
  return "sagittal";
}
